/**
 * Exit Intent Popup
 * Triggers when user's mouse leaves the viewport (about to close tab/navigate away)
 */

import React, { useState, useEffect } from 'react';
import BasePopup from './BasePopup';
import PopupForm from './PopupForm';

export interface ExitIntentPopupProps {
  enabled?: boolean;
  delay?: number; // Minimum time on page before showing (ms)
}

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({
  enabled = true,
  delay = 5000, // Show after 5 seconds on page
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    // Only stop showing if user has actually signed up
    const hasSignedUp = localStorage.getItem('landListingSignup');
    if (hasSignedUp) {
      setHasShown(true);
      return;
    }

    // Wait for delay before enabling exit detection
    const delayTimer = setTimeout(() => {
      setIsReady(true);
    }, delay);

    return () => clearTimeout(delayTimer);
  }, [enabled, delay]);

  useEffect(() => {
    if (!isReady || hasShown) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Check if mouse is leaving from the top of the viewport
      if (e.clientY <= 0) {
        setIsOpen(true);
        setHasShown(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isReady, hasShown]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSuccess = () => {
    setIsOpen(false);
    // Mark that user has signed up - don't show any popups again
    localStorage.setItem('landListingSignup', 'true');
    alert('Success! Check your email for the free checklist.');
  };

  if (!enabled) return null;

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Wait! Before you go..."
      subtitle="Get our free Builder's Land Evaluation Checklist"
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          Download our comprehensive checklist used by professional builders to evaluate
          development-ready land parcels. Plus, get weekly land opportunities delivered to your inbox.
        </p>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900 mb-2">What's included:</h3>
          <ul className="text-sm text-purple-800 space-y-1">
            <li>✓ Site evaluation criteria</li>
            <li>✓ Zoning & permits checklist</li>
            <li>✓ Cost estimation framework</li>
            <li>✓ Red flags to avoid</li>
          </ul>
        </div>

        <PopupForm
          source="exit_popup"
          onSuccess={handleSuccess}
          buttonText="Send Me The Checklist (Free)"
        />
      </div>
    </BasePopup>
  );
};

export default ExitIntentPopup;
